import React, { useCallback, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock } from "lucide-react";
import { COLORS } from "../constants";
import { NAV_ITEMS } from "../navConfig";
import { pill, fontSerif } from "./styles";

/**
 * NavButton — single nav pill with hover-driven "Coming Soon" tooltip
 * for locked items. The tooltip is purely a hint; clicking still routes
 * to /{id} which renders the Under Construction modal.
 */
function NavButton({ item, active, onClick, tooltipAbove = false }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const wrapRef = useRef();
  const showTip = item.locked && (hovered || focused);

  return (
    <div
      ref={wrapRef}
      style={{ position: "relative", display: "flex" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        data-testid={`zone-nav-${item.id}`}
        aria-current={active ? "page" : undefined}
        aria-describedby={item.locked ? `nav-tip-${item.id}` : undefined}
        onClick={onClick}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          padding: "8px 16px",
          borderRadius: 999,
          border: "none",
          cursor: "pointer",
          backgroundColor: active ? COLORS.gold : "transparent",
          color: active
            ? COLORS.navyDeep
            : item.locked
            ? "rgba(243,234,212,0.7)"
            : COLORS.ivory,
          fontFamily: fontSerif,
          fontSize: 12,
          letterSpacing: 1.5,
          textTransform: "uppercase",
          fontWeight: active ? 700 : 500,
          display: "flex",
          alignItems: "center",
          gap: 6,
          transition: "background-color .2s, color .2s",
          position: "relative",
        }}
      >
        {item.locked && (
          <Lock
            size={11}
            color={active ? COLORS.navyDeep : COLORS.gold}
            aria-hidden="true"
          />
        )}
        {item.label}
      </button>

      {/* Hover tooltip — Coming Soon */}
      {item.locked && (
        <div
          id={`nav-tip-${item.id}`}
          role="tooltip"
          data-testid={`zone-nav-tip-${item.id}`}
          aria-hidden={!showTip}
          style={{
            position: "absolute",
            ...(tooltipAbove
              ? { bottom: "calc(100% + 10px)" }
              : { top: "calc(100% + 10px)" }),
            left: "50%",
            transform: `translateX(-50%) translateY(${
              showTip ? 0 : tooltipAbove ? 4 : -4
            }px)`,
            opacity: showTip ? 1 : 0,
            pointerEvents: "none",
            background: COLORS.gold,
            color: COLORS.navyDeep,
            padding: "4px 12px",
            borderRadius: 999,
            fontFamily: fontSerif,
            fontSize: 10,
            letterSpacing: 2.5,
            textTransform: "uppercase",
            fontWeight: 700,
            whiteSpace: "nowrap",
            transition: "opacity .18s ease-out, transform .18s ease-out",
            boxShadow: "0 8px 20px rgba(0,0,0,0.35), 0 0 18px rgba(230,180,80,0.25)",
            zIndex: 20,
          }}
        >
          {/* arrow */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              ...(tooltipAbove ? { bottom: -5 } : { top: -5 }),
              left: "50%",
              transform: "translateX(-50%) rotate(45deg)",
              width: 8,
              height: 8,
              background: COLORS.gold,
            }}
          />
          Coming Soon
        </div>
      )}
    </div>
  );
}

/**
 * TopNav — site-section navigation. By default sits in row 2 of the header
 * (top center). When `dock="bottom"` it floats at the bottom-center of the
 * viewport with a soft glass card so it doesn't collide with the right-edge
 * control stack.
 */
export default function TopNav({ dock = "top" }) {
  const navigate = useNavigate();
  const location = useLocation();

  const activeId = (() => {
    const p = location.pathname;
    if (p === "/" || p.startsWith("/landmark")) return "quad";
    const seg = p.replace(/^\//, "").split("/")[0];
    const found = NAV_ITEMS.find((n) => n.id === seg);
    return found ? found.id : "quad";
  })();

  const handleClick = useCallback(
    (item) => {
      // Campus Quad (or any item with an externalUrl) opens in a new tab so
      // the visitor lands on the full-viewport standalone experience without
      // losing their current tab context.
      if (item.externalUrl) {
        window.open(item.externalUrl, "_blank", "noopener,noreferrer");
        return;
      }
      if (location.pathname === item.path) return;
      navigate(item.path);
    },
    [navigate, location.pathname]
  );

  const navStyle =
    dock === "bottom"
      ? {
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 78,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
          zIndex: 5,
          padding: "0 16px",
        }
      : {
          position: "absolute",
          top: 96,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
          zIndex: 5,
        };

  const cardStyle =
    dock === "bottom"
      ? {
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 4,
          padding: 6,
          pointerEvents: "auto",
          maxWidth: "100%",
          background: "rgba(11,21,48,0.55)",
          border: `1px solid ${COLORS.navyMid}`,
          borderRadius: 999,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          boxShadow:
            "0 16px 48px rgba(0,0,0,0.35), 0 0 28px rgba(230,180,80,0.06)",
        }
      : {
          display: "flex",
          gap: 4,
          padding: 4,
          pointerEvents: "auto",
          ...pill,
        };

  return (
    <nav
      data-testid="zone-top-nav"
      aria-label="Site sections"
      style={navStyle}
    >
      <div style={cardStyle}>
        {NAV_ITEMS.map((item) => (
          <NavButton
            key={item.id}
            item={item}
            active={item.id === activeId}
            onClick={() => handleClick(item)}
            tooltipAbove={dock === "bottom"}
          />
        ))}
      </div>
    </nav>
  );
}
